import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ProvisioningJobTable from './ProvisioningJobTable';

const FacilityBulkJobsPanel = ({
  jobs,
  loading,
  error,
  submitting,
  onRefresh,
  onRetry,
  onRefreshFacilities,
  embedded = false,
}) => {
  const handleRefresh = async () => {
    await onRefresh?.({ background: true });
    onRefreshFacilities?.({ background: true });
  };

  const renderBody = () => {
    if (loading && jobs.length === 0) {
      return <LinearProgress data-testid="facility-lms-bulk-jobs-loading" />;
    }
    if (jobs.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No bulk facility provisioning jobs yet. Select facilities and start bulk provisioning from the bulk bar.
        </Typography>
      );
    }
    return (
      <ProvisioningJobTable jobs={jobs} submitting={submitting} onRetry={onRetry} variant="bulk" />
    );
  };

  return (
    <Paper
      variant="outlined"
      elevation={embedded ? 0 : 1}
      sx={
        embedded
          ? { p: 0, border: 0, backgroundColor: 'transparent' }
          : { p: 2, mb: 2, borderColor: 'divider', borderRadius: 2 }
      }
      data-testid="facility-lms-bulk-jobs-panel"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            Bulk facility provisioning jobs
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Async location bulk runs (accounts.csv → Canvas SIS import). Use Refresh to update job status.
          </Typography>
        </Box>
        <Tooltip title="Refresh job status">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              disabled={loading || submitting}
              onClick={handleRefresh}
              data-testid="facility-lms-bulk-jobs-refresh"
            >
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          Failed to load bulk provisioning jobs.
        </Alert>
      )}

      {renderBody()}
    </Paper>
  );
};

FacilityBulkJobsPanel.propTypes = {
  jobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitting: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onRefreshFacilities: PropTypes.func,
  embedded: PropTypes.bool,
};

FacilityBulkJobsPanel.defaultProps = {
  loading: false,
  error: null,
  submitting: false,
  onRefreshFacilities: null,
  embedded: false,
};

export default FacilityBulkJobsPanel;
